import { useState, useEffect, useRef } from "react";
import {
  Calendar,
  Plus,
  Search,
  Filter,
  MapPin,
  Users,
  Clock,
  Activity,
  X,
  ChevronDown,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchCampaigns } from "../../services/api";

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Filter popup state
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterOrganizer, setFilterOrganizer] = useState("all");

  const filterPopupRef = useRef(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Handle clicking outside filter popup to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target)
      ) {
        setShowFilterPopup(false);
      }
    };

    if (showFilterPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterPopup]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);

      const campaignsData = await fetchCampaigns();
      // console.log("Fetched campaigns:", campaignsData);

      // Map the API data to the expected format
      const mappedCampaigns = campaignsData.map((campaign) => ({
        id: campaign.id,
        name:
          campaign.C_BPartner_ID?.identifier ||
          campaign.name ||
          campaign.Name ||
          "Unnamed Campaign",
        location:
          campaign.venue || campaign.Address1 || "Location not specified",
        address: campaign.venue || campaign.Address1 || "Address not specified",
        date: campaign.startDate || campaign.StartDate || "Date not specified",
        time: campaign.Time || campaign.StartTime || "Time not specified",
        status: getCampaignStatus(campaign),
        targetDonors: campaign.targetQty || campaign.TargetQty || 0,
        registeredDonors: 0, // This would need a separate API call
        completedDonations: 0, // This would need a separate API call
        description:
          campaign.description ||
          campaign.Description ||
          "No description available",
        organizer:
          campaign.organization || campaign.AD_Org_ID?.identifier || "Unknown",
        contactPerson:
          campaign.position ||
          campaign.Position ||
          "Contact person not specified",
        contactNumber:
          campaign.phone || campaign.Phone || "Phone not specified",
        email: campaign.email || campaign.EMail || "Email not specified",
        district:
          campaign.district ||
          campaign.n_district_ID?.identifier ||
          "District not specified",
        localGovernment:
          campaign.localGovernment ||
          campaign.n_locgov_ID?.identifier ||
          "Local government not specified",
        isActive: campaign.isActive || campaign.IsActive === "Y",
        docStatus: campaign.DocStatus,
        created: campaign.created || campaign.Created,
        costs: campaign.Costs || 0,
      }));

      setCampaigns(mappedCampaigns);
    } catch (err) {
      console.error("Failed to load campaigns:", err);
      setError(err.message || "Failed to load campaigns");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const getCampaignStatus = (campaign) => {
    // Use DocStatus.id or DocStatus.identifier
    const docStatus =
      campaign.DocStatus?.id ||
      campaign.DocStatus?.identifier ||
      campaign.DocStatus;

    switch (docStatus) {
      case "O":
      case "Booked":
        return "Active";
      case "CO":
      case "Completed":
        return "Completed";
      case "CL":
      case "Closed":
      case "VO":
      case "Voided":
        return "Cancelled";
      default:
        // Fallback to date logic if needed
        const startDate = new Date(campaign.startDate || campaign.StartDate);
        const endDate = new Date(campaign.EndDate);
        const now = new Date();
        if (startDate > now) return "Upcoming";
        if (endDate < now) return "Completed";
        return "Active";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "upcoming":
        return "bg-blue-100 text-blue-800";
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getProgressPercentage = (campaign) => {
    if (campaign.status === "Completed") return 100;
    if (campaign.targetDonors === 0) return 0;
    return Math.round(
      (campaign.registeredDonors / campaign.targetDonors) * 100
    );
  };

  // Get unique values for filter dropdowns
  const getUniqueDistricts = () => {
    const districts = campaigns
      .map((campaign) => campaign.district)
      .filter((district) => district && district !== "District not specified");
    return [...new Set(districts)].sort();
  };

  const getUniqueOrganizers = () => {
    const organizers = campaigns
      .map((campaign) => campaign.organizer)
      .filter((organizer) => organizer && organizer !== "Unknown");
    return [...new Set(organizers)].sort();
  };

  // Filter actions
  const clearAllFilters = () => {
    setFilterStatus("all");
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setFilterDistrict("all");
    setFilterOrganizer("all");
  };

  const applyFilters = () => {
    setShowFilterPopup(false);
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.organizer.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      campaign.status.toLowerCase() === filterStatus.toLowerCase();

    const matchesDateRange = (() => {
      if (!filterDateFrom && !filterDateTo) return true;
      const campaignDate = new Date(campaign.date);
      if (isNaN(campaignDate.getTime())) return true; // Invalid date, include it

      if (filterDateFrom && filterDateTo) {
        const fromDate = new Date(filterDateFrom);
        const toDate = new Date(filterDateTo);
        // Set time to start/end of day for proper comparison
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        return campaignDate >= fromDate && campaignDate <= toDate;
      } else if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return campaignDate >= fromDate;
      } else if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        return campaignDate <= toDate;
      }
      return true;
    })();

    const matchesDistrict =
      filterDistrict === "all" ||
      campaign.district.toLowerCase().includes(filterDistrict.toLowerCase());

    const matchesOrganizer =
      filterOrganizer === "all" ||
      campaign.organizer.toLowerCase().includes(filterOrganizer.toLowerCase());

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDateRange &&
      matchesDistrict &&
      matchesOrganizer
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={() => loadCampaigns()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {" "}
      {/* Responsive vertical spacing */}
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 mr-2 text-red-600" />
            Campaigns
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage blood donation campaigns and events
          </p>
        </div>
        <button
          onClick={() => (window.location.href = "/create-campaign")}
          className="btn-primary flex items-center mt-2 sm:mt-0"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </button>
      </div>
      {/* Filters and Search */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by campaign name, location, or organizer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="flex gap-2 relative">
            <button
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              className="btn-secondary flex items-center text-sm sm:text-base relative"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
              {(filterStatus !== "all" ||
                filterDateFrom ||
                filterDateTo ||
                filterDistrict !== "all" ||
                filterOrganizer !== "all") && (
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Filter Popup */}
            {showFilterPopup && (
              <div
                ref={filterPopupRef}
                className="filter-popup absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Advanced Filters
                  </h3>
                  <button
                    onClick={() => setShowFilterPopup(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          From
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={filterDateFrom}
                            onChange={(date) => setFilterDateFrom(date)}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Select date"
                            showPopperArrow={false}
                            popperClassName="datepicker-popper"
                            popperPlacement="bottom-start"
                            maxDate={filterDateTo || undefined}
                            wrapperClassName="w-full"
                          />
                          <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          To
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={filterDateTo}
                            onChange={(date) => setFilterDateTo(date)}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Select date"
                            showPopperArrow={false}
                            popperClassName="datepicker-popper"
                            popperPlacement="bottom-start"
                            minDate={filterDateFrom || undefined}
                            wrapperClassName="w-full"
                          />
                          <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* District Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District
                    </label>
                    <select
                      value={filterDistrict}
                      onChange={(e) => setFilterDistrict(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Districts</option>
                      {getUniqueDistricts().map((district) => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Organizer Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organizer
                    </label>
                    <select
                      value={filterOrganizer}
                      onChange={(e) => setFilterOrganizer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Organizers</option>
                      {getUniqueOrganizers().map((organizer) => (
                        <option key={organizer} value={organizer}>
                          {organizer}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={clearAllFilters}
                      className="flex-1 px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Campaigns
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.filter((c) => c.status === "Active").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Target</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, c) => sum + c.targetDonors, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Completed
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, c) => sum + c.completedDonations, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {filteredCampaigns.map((campaign) => (
          <div
            key={campaign.id}
            className="card hover:shadow-lg transition-shadow duration-200 p-2 sm:p-4 md:p-6"
          >
            <div className="p-2 sm:p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {campaign.name}
                  </h3>
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    {campaign.location}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    campaign.status
                  )}`}
                >
                  {campaign.status}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>
                    {campaign.date !== "Date not specified"
                      ? new Date(campaign.date).toLocaleDateString()
                      : "Date not specified"}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>{campaign.time}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 mr-2" />
                  <span>{campaign.organizer}</span>
                </div>
                {campaign.district &&
                  campaign.district !== "District not specified" && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">District:</span>{" "}
                      {campaign.district}
                    </div>
                  )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-lg font-semibold text-gray-900">
                    {campaign.targetDonors}
                  </div>
                  <div className="text-xs text-gray-500">Target</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-lg font-semibold text-gray-900">
                    {campaign.completedDonations}
                  </div>
                  <div className="text-xs text-gray-500">Completed</div>
                </div>
              </div>

              {/* Actions */}
              {/* <div className="flex space-x-2">
                {campaign.status === "Upcoming" && (
                  <button className="flex-1 btn-primary text-sm">Edit</button>
                )}
              </div> */}
            </div>
          </div>
        ))}
      </div>
      {filteredCampaigns.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No campaigns found</p>
          <p className="text-sm text-gray-400 mt-2">
            {searchTerm || filterStatus !== "all"
              ? "Try adjusting your search or filter criteria"
              : "No campaigns have been created yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
